"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { ArrowUpRightIcon } from "@heroicons/react/24/solid";

interface NavItem {
    label: string;
    href: string;
    isButton?: boolean;
    icon?: React.ReactNode;
}

interface NavbarProps {
    items?: NavItem[];
    className?: string;
}

export default function Navbar({
    items = defaultNavItems,
    className = "",
}: NavbarProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768 && isOpen) {
                setIsOpen(false);
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [isOpen]);

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 rounded-3xl mt-4 py-3 border border-transparent ${isScrolled
                    ? "bg-neutral-900/60 backdrop-blur-md mx-96 border-neutral-800"
                    : "bg-transparent border-transparent mx-[26rem]"
                } ${className}`}
        >
            <div className="container mx-auto px-4 md:px-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <a href="https://octant.gg" className="flex items-center">
                            <img src={isScrolled ? "/logo.png" : "/logo.png"} alt="Octant" className="h-8" />
                        </a>

                        <div className="hidden md:flex items-center gap-6">
                            <a href="/" className="text-[#f5f3f0] hover:text-[#f5f3f0]/85 transition-colors">
                                Blog
                            </a>
                        </div>
                    </div>

                    <nav className="hidden md:flex items-center gap-6">
                        {items.map((item, index) => (
                            <a
                                key={index}
                                href={item.href}
                                className={
                                    item.isButton
                                        ? "px-4 py-2 rounded-2xl bg-[#f5f3f0] border border-[#f5f3f0]/85 text-gray-900 transition-all duration-200 hover:bg-[#f5f3f0]/85 flex items-center gap-2"
                                        : "text-[#f5f3f0] hover:text-[#f5f3f0]/85 transition-colors"
                                }
                            >
                                {item.label}
                                {item.icon && item.icon}
                            </a>
                        ))}
                    </nav>

                    <button
                        className="md:hidden text-white p-2 focus:outline-none"
                        onClick={() => setIsOpen(!isOpen)}
                        aria-label={isOpen ? "Close menu" : "Open menu"}
                    >
                        {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </div>

            <div
                className={`md:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 transition-all duration-300 ${isOpen
                        ? "max-h-screen py-4"
                        : "max-h-0 py-0 overflow-hidden border-b-0"
                    }`}
            >
                <nav className="container mx-auto px-4 flex flex-col gap-4">
                    <a
                        href="/games/minecraft"
                        className="text-gray-900 hover:text-gray-600 py-3 border-b border-gray-200/50 transition-colors"
                        onClick={() => setIsOpen(false)}
                    >
                        Minecraft
                    </a>
                    <a
                        href="https://docs.octant.gg"
                        className="text-gray-900 hover:text-gray-600 py-3 border-b border-gray-200/50 transition-colors"
                        onClick={() => setIsOpen(false)}
                    >
                        Documentation
                    </a>

                    {items.map((item, index) => (
                        <a
                            key={index}
                            href={item.href}
                            className={
                                item.isButton
                                    ? "px-4 py-3 rounded-lg bg-gray-100 border border-gray-200 text-gray-900 text-center transition-all duration-200 hover:bg-gray-200 flex items-center justify-center gap-2"
                                    : "text-gray-900 hover:text-gray-600 py-3 border-b border-gray-200/50 transition-colors"
                            }
                            onClick={() => setIsOpen(false)}
                        >
                            {item.icon && item.icon}
                            {item.label}
                        </a>
                    ))}
                </nav>
            </div>
        </header>
    );
}

const defaultNavItems: NavItem[] = [
    {
        label: "Posts",
        href: "/posts",
        isButton: true,
        icon: <ArrowUpRightIcon className="h-4 w-4" />,
    },
];